import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useSidebar,
} from "@beweegbaas/ui";
import { Link, useLocation, useParams, useRouter } from "@tanstack/react-router";
import { BorderBeam } from "border-beam";
import { AnimatePresence, motion } from "motion/react";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useEffect, useState, type ComponentProps, type ComponentType, type SVGProps } from "react";
import { m } from "#/paraglide/messages";
import { AdminSidebarBanner, useIsAdminSession } from "#/modules/admin/components/admin-banners";
import { AdminSchooljaarSelect } from "#/modules/admin/components/admin-schooljaar-select";
import { useSchoolLayoutData, useSchools } from "#/modules/generic.user/use-schools";
import { SidebarLogoLink } from "#/components/sidebar-logo";
import { logoutFn } from "#/modules/util.auth/auth.functions";
import {
  useActiveLessen,
  useHasReadLatestReleaseNotes,
  useUnreadTicketCount,
  useUser,
} from "#/modules/util.auth/use-user";
import { useLocaleParams } from "#/modules/util.helpers/route-href";
import { getLicentieTypeTranslation } from "#/modules/util.i18n/enum-mapping";
import { useTheme } from "#/modules/util.theme/theme";

function useSchoolLinkParams() {
  const localeLinkParams = useLocaleParams();
  const { school } = useParams({ strict: false });
  return { ...localeLinkParams, school: school! };
}

/** Closes the mobile sheet when a sidebar link navigates. */
function useCloseMobile() {
  const { setOpenMobile } = useSidebar();
  return () => setOpenMobile(false);
}

/**
 * Active = the URL we're on (or navigating to). Deliberately NOT matchRoute: its pending
 * semantics compare against the router's previous resolvedLocation, so during a slow navigation
 * the indicator rendered stale. location.pathname is a single always-reactive source and moves
 * the indicator to the clicked item immediately.
 *
 * `ownsSection` treats the link as owning its whole parent section: the last path segment is
 * dropped before matching (e.g. `/insights/averages` link → active on all of `/insights/*`).
 */
function useIsRouteActive() {
  const location = useLocation();
  const router = useRouter();
  const schoolLinkParams = useSchoolLinkParams();

  return (
    to: string,
    {
      ownsSection = false,
      params = {},
    }: { ownsSection?: boolean; params?: Record<string, string> } = {},
  ) => {
    const path = router.buildLocation({
      to,
      params: { ...schoolLinkParams, ...params },
    }).pathname;
    const base = ownsSection ? path.slice(0, path.lastIndexOf("/")) : path;
    return location.pathname === base || location.pathname.startsWith(`${base}/`);
  };
}

function SchoolSwitcher() {
  const schools = useSchools();
  const schoolLinkParams = useSchoolLinkParams();
  const closeMobile = useCloseMobile();
  const { isMobile } = useSidebar();
  const currentSchool = schools.find((s) => s.slug === schoolLinkParams.school);

  // Admins browse foreign schools and have no scholen of their own — no switcher for them.
  if (!currentSchool) {
    return (
      <SidebarMenu className="group-data-[collapsible=icon]:hidden">
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" render={<div />} className="pointer-events-none">
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{schoolLinkParams.school}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu className="group-data-[collapsible=icon]:hidden">
      <SidebarMenuItem>
        <DropdownMenu>
          {/* No aria-label: the accessible name is the school name itself (and "Menu openen"
              must stay unique to the user-menu trigger). */}
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                className="data-open:bg-sidebar-accent px-3 py-1 border data-open:text-sidebar-accent-foreground"
              />
            }
          >
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{currentSchool.name}</span>
              <span className="truncate text-xs text-muted-foreground">
                {getLicentieTypeTranslation(currentSchool.licentieType)}
              </span>
            </div>
            <IconMaterialSymbolsExpandAll className="ml-auto" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
            className="w-fit max-w-80"
          >
            {/* Base UI requires group labels to live inside a Menu.Group. */}
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                {m["nav.school_switcher.title"]()}
              </DropdownMenuLabel>
              {schools.map((school) => (
                <DropdownMenuItem
                  key={school.id}
                  render={
                    <Link
                      to="/$locale/{-$schoolyear}/$school/lessons/today"
                      params={{ ...schoolLinkParams, school: school.slug }}
                      onClick={closeMobile}
                    />
                  }
                >
                  <span className="truncate">{school.name}</span>
                  {school.licentieType === "Proefperiode" && (
                    <Badge variant="blue">{m["nav.school_switcher.trial_badge"]()}</Badge>
                  )}
                  {school.licentieType === "Showcase" && <Badge variant="blue">Showcase</Badge>}
                  {school.id === currentSchool.id && (
                    <IconMaterialSymbolsCheckCircleRounded className="text-muted-foreground ml-auto" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                render={
                  <Link
                    to="/$locale/{-$schoolyear}/$school/add-school"
                    params={schoolLinkParams}
                    onClick={closeMobile}
                  />
                }
              >
                <div
                  className="flex size-6 items-center justify-center rounded-md border
                    bg-transparent"
                >
                  <IconMaterialSymbolsAdd />
                </div>
                <div className="text-muted-foreground font-medium">
                  {m["common.add_another_school"]()}
                </div>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/**
 * Icon-collapsed fallback for SubscriptionCard: the card can't shrink to an icon, so in
 * collapsed mode the licentie status shows as a tooltip'd menu item instead.
 */
function SubscriptionMenuItem() {
  const schoolLinkParams = useSchoolLinkParams();
  const closeMobile = useCloseMobile();
  const loaderData = useSchoolLayoutData();
  const showSubscriptionReminder = loaderData?.showSubscriptionReminder ?? false;
  const subscriptionInProgress = loaderData?.subscriptionInProgress ?? false;

  if (!subscriptionInProgress && !showSubscriptionReminder) return null;

  const label = subscriptionInProgress
    ? m["app.subscription.pending"]()
    : m["enums.LicentieType.Proefperiode"]();
  return (
    <SidebarMenuItem className="hidden group-data-[collapsible=icon]:block">
      <SidebarMenuButton
        tooltip={label}
        render={
          <Link
            to="/$locale/{-$schoolyear}/$school/end-test-period"
            params={schoolLinkParams}
            onClick={closeMobile}
          />
        }
      >
        {subscriptionInProgress ? (
          <IconMaterialSymbolsHourglassEmpty />
        ) : (
          <IconMaterialSymbolsTimerOutline />
        )}
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * Licentie status card — proefperiode reminder or pending abonnement request; the state is
 * important enough that it shouldn't read as just another nav link. `subscriptionInProgress`
 * wins over the reminder, mirroring the old header behaviour. Hidden in icon-collapsed mode;
 * SubscriptionMenuItem covers that.
 */
const SUBSCRIPTION_CARD_CLOSED_KEY = "subscription-card-closed-until";
const SUBSCRIPTION_CARD_CLOSED_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Dismissed state persists in localStorage as an expiry timestamp (localStorage has no native
 * TTL) — the card reappears after a week. Read in an effect so SSR and hydration render open.
 */
function useSubscriptionCardClosed() {
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    const closedUntil = Number(localStorage.getItem(SUBSCRIPTION_CARD_CLOSED_KEY));
    if (closedUntil > Date.now()) setClosed(true);
    else localStorage.removeItem(SUBSCRIPTION_CARD_CLOSED_KEY);
  }, []);
  const close = () => {
    localStorage.setItem(
      SUBSCRIPTION_CARD_CLOSED_KEY,
      String(Date.now() + SUBSCRIPTION_CARD_CLOSED_MS),
    );
    setClosed(true);
  };
  return [closed, close] as const;
}

function SubscriptionCard() {
  const schoolLinkParams = useSchoolLinkParams();
  const closeMobile = useCloseMobile();
  const loaderData = useSchoolLayoutData();
  const [closed, close] = useSubscriptionCardClosed();
  const showSubscriptionReminder = loaderData?.showSubscriptionReminder ?? false;
  const subscriptionInProgress = loaderData?.subscriptionInProgress ?? false;

  if (!subscriptionInProgress && !showSubscriptionReminder) return null;

  const button = (
    <Button
      size="sm"
      variant={closed || subscriptionInProgress ? "outline" : "default"}
      className="w-full"
      nativeButton={false}
      render={
        <Link
          to="/$locale/{-$schoolyear}/$school/end-test-period"
          params={schoolLinkParams}
          onClick={closeMobile}
        />
      }
    >
      {subscriptionInProgress
        ? m["app.subscription.view_status"]()
        : m["app.subscription.start_abonnement"]()}
    </Button>
  );

  if (closed) {
    return <div className="group-data-[collapsible=icon]:hidden">{button}</div>;
  }

  return (
    <Card size="sm" className="group-data-[collapsible=icon]:hidden relative">
      <CardHeader>
        <CardTitle>
          {subscriptionInProgress
            ? m["app.subscription.abonnement_in_behandeling"]()
            : m["enums.LicentieType.Proefperiode"]()}
        </CardTitle>
        <CardDescription>
          {subscriptionInProgress
            ? m["app.subscription.verwerkersovereenkomst_pending"]()
            : m["app.subscription.trial_description"]()}
        </CardDescription>

        <Button
          className={"absolute top-2 right-2"}
          size="icon-xs"
          variant="ghost"
          aria-label={m["app.actions.close"]()}
          onClick={close}
        >
          <IconMaterialSymbolsClose />
        </Button>
      </CardHeader>
      <CardContent>{button}</CardContent>
    </Card>
  );
}

/**
 * Admin-only schooljaar picker. Renders nothing for regular users (the select gates itself);
 * hidden in icon-collapsed mode since a Select can't collapse to an icon.
 */
function SchooljaarSelect() {
  return (
    <div className="group-data-[collapsible=icon]:hidden empty:hidden">
      <AdminSchooljaarSelect />
    </div>
  );
}

/**
 * Start-les / active-les call to action — the single primary action of the app. Deliberately the
 * app's own `Button` (the header CTA it replaces), not a `SidebarMenuButton`: it's a primary
 * action, not a nav item. In icon-collapsed mode it shrinks to an icon button with a tooltip
 * (mirroring `SidebarMenuButton`'s tooltip behaviour).
 */
function LessonAction() {
  const schoolLinkParams = useSchoolLinkParams();
  const closeMobile = useCloseMobile();
  const { state, isMobile } = useSidebar();
  const activeLessen = useActiveLessen();
  const activeLes = activeLessen.find((les) => les.schoolSlug === schoolLinkParams.school);
  const pathname = useLocation({ select: (location) => location.pathname });
  const isOnActiveLessonPage = activeLes
    ? pathname.includes(`/lesson/${activeLes.lesId}/teacher`)
    : false;

  const label = activeLes ? m["les.actief.actieve_les"]() : m["les.start.title"]();
  const collapseClasses = `w-full group-data-[collapsible=icon]:size-10
    group-data-[collapsible=icon]:min-w-10 group-data-[collapsible=icon]:p-0`;

  const button = activeLes ? (
    <Button
      render={
        <Link
          to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores"
          params={{ ...schoolLinkParams, lesson: activeLes.lesId }}
          onClick={closeMobile}
        />
      }
      nativeButton={false}
      disabled={isOnActiveLessonPage}
      contentClassName="justify-between flex w-full group-data-[collapsible=icon]:justify-center"
      className={cn(
        `bg-linear-to-br from-blue-300 to-orange-400 hover:from-blue-400
        hover:to-orange-500`,
        isOnActiveLessonPage && "pointer-events-none",
        collapseClasses,
      )}
    >
      <span className="group-data-[collapsible=icon]:hidden">{label}</span>
      <span className="relative block size-5">
        <IconSvgSpinnersPulse
          className="absolute top-1/2 left-1/2 size-10 -translate-x-1/2 -translate-y-1/2
          text-white opacity-50"
        />
        <IconMaterialSymbolsPlayArrowRounded className="relative size-5" />
      </span>
    </Button>
  ) : (
    <Button
      variant="special"
      render={
        // A les starts on the lesdag: from its lesuur, or from an empty one.
        <Link
          to="/$locale/{-$schoolyear}/$school/lessons/today"
          params={schoolLinkParams}
          onClick={closeMobile}
        />
      }
      nativeButton={false}
      className={cn(collapseClasses)}
      contentClassName="justify-between flex w-full group-data-[collapsible=icon]:justify-center"
    >
      <span className="group-data-[collapsible=icon]:hidden">{label}</span>
      <IconMaterialSymbolsPlayArrowRounded className="size-5" />
    </Button>
  );

  return (
    <BorderBeam
      size="md"
      theme="dark"
      staticColors
      active={!isOnActiveLessonPage && (state !== "collapsed" || isMobile)}
      className="beam-brand squircle w-full group-data-[collapsible=icon]:w-fit
        group-data-[collapsible=icon]:self-center"
    >
      <Tooltip>
        <TooltipTrigger render={button} />
        <TooltipContent side="right" align="center" hidden={state !== "collapsed" || isMobile}>
          {label}
        </TooltipContent>
      </Tooltip>
    </BorderBeam>
  );
}

interface NavItem {
  /** Function so paraglide messages resolve at render time (locale switches). */
  label: () => string;
  to: string;
  id: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Match the whole parent section instead of the exact link (see useIsRouteActive). */
  ownsSection?: boolean;
  params?: Record<string, string>;
}

// The Inzichten item links to its landing tab but owns the whole /insights section.
const MAIN_NAV_ITEMS: NavItem[] = [
  {
    // Opens on today's lessen, where the day starts, and owns the whole /lessons section.
    label: () => m["nav.lessen"](),
    to: "/$locale/{-$schoolyear}/$school/lessons/today",
    id: "lessons",
    icon: IconMaterialSymbolsExercise,
    ownsSection: true,
  },
  {
    // The rooster lives with the lessen it makes, as a dialog on Mijn lesdag and Mijn lesweek.
    label: () => m["planning.view_jaar"](),
    to: "/$locale/{-$schoolyear}/$school/planning/year",
    id: "planning",
    icon: IconMaterialSymbolsCalendarMonthOutline,
    ownsSection: true,
  },
  {
    label: () => m["nav.klassen"](),
    to: "/$locale/{-$schoolyear}/$school/classes",
    id: "klassen",
    icon: IconMaterialSymbolsGroupsOutline,
  },
  {
    label: () => m["nav.leerlingen"](),
    to: "/$locale/{-$schoolyear}/$school/students/overview",
    id: "students",
    icon: IconMaterialSymbolsFaceOutline,
    // Leerling detail lives at /students/$id/* — keep the item highlighted there too.
    ownsSection: true,
  },
  {
    label: () => m["nav.beweegactiviteiten"](),
    to: "/$locale/{-$schoolyear}/$school/activities",
    id: "goals",
    icon: IconMaterialSymbolsSportsHandball,
  },
  {
    label: () => m["common.labels.sociaal_emotionele_themas"](),
    to: "/$locale/{-$schoolyear}/$school/social-emotional",
    id: "social-emotional",
    icon: IconMaterialSymbolsSentimentSatisfiedOutline,
  },
  {
    label: () => m["nav.rapporten"](),
    to: "/$locale/{-$schoolyear}/$school/reports",
    id: "reports",
    icon: IconMaterialSymbolsLabProfileOutline,
  },
  {
    label: () => m["nav.insights"](),
    to: "/$locale/{-$schoolyear}/$school/insights/averages",
    id: "insights",
    icon: IconMaterialSymbolsMonitoring,
    ownsSection: true,
  },
  {
    // Links to the info tab but owns the whole /detail section (import, docenten, ...).
    label: () => m["nav.school_management"](),
    to: "/$locale/{-$schoolyear}/$school/detail/info",
    id: "school",
    icon: IconMaterialSymbolsSchoolOutline,
    ownsSection: true,
  },
];

/**
 * Label of the sidebar nav item that owns the current URL. Used as the mobile page-title
 * fallback next to the sidebar trigger (`Page.Header`) on pages without an explicit title.
 */
export function useActiveNavLabel(): string | undefined {
  const { school } = useParams({ strict: false });
  const isRouteActive = useIsRouteActive();
  if (!school) return undefined;
  const active = MAIN_NAV_ITEMS.find((item) =>
    isRouteActive(item.to, { ownsSection: item.ownsSection, params: item.params }),
  );
  return active?.label();
}

function NavGroup({ label, items }: { label?: string; items: NavItem[] }) {
  const schoolLinkParams = useSchoolLinkParams();
  const closeMobile = useCloseMobile();
  const isRouteActive = useIsRouteActive();

  return (
    <SidebarGroup>
      {label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.id}>
            <SidebarMenuButton
              isActive={isRouteActive(item.to, {
                ownsSection: item.ownsSection,
                params: item.params,
              })}
              tooltip={item.label()}
              render={
                <Link
                  to={item.to}
                  params={{ ...schoolLinkParams, ...item.params }}
                  onClick={closeMobile}
                />
              }
            >
              <item.icon />
              <span>{item.label()}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

// FAQ + ticket funnel (/help). Lives outside MAIN_NAV_ITEMS so it renders in its own group below
// a separator, set apart from the school sections.
const HELP_NAV_ITEM: NavItem = {
  label: () => m["help.title"](),
  to: "/$locale/{-$schoolyear}/$school/help",
  id: "help",
  icon: IconMaterialSymbolsHelpOutline,
};

/** "Vragen en suggesties" link directly below the main nav. Hidden for admins. */
function NavHelp() {
  const isAdmin = useSchoolLayoutData()?.isAdmin ?? false;
  if (isAdmin) return null;

  return (
    <>
      <SidebarSeparator />
      <NavGroup items={[HELP_NAV_ITEM]} />
    </>
  );
}

/** Bottom-aligned licentie status: the proefperiode card (or its collapsed fallback item). */
function NavSecondary(props: ComponentProps<typeof SidebarGroup>) {
  const loaderData = useSchoolLayoutData();
  const isAdmin = loaderData?.isAdmin ?? false;
  const hasSubscriptionStatus =
    (loaderData?.showSubscriptionReminder ?? false) ||
    (loaderData?.subscriptionInProgress ?? false);
  if (isAdmin || !hasSubscriptionStatus) return null;

  return (
    <SidebarGroup {...props}>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          <SubscriptionMenuItem />
        </SidebarMenu>
        <SubscriptionCard />
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function NavUser() {
  const user = useUser();
  const schoolLinkParams = useSchoolLinkParams();
  const localeLinkParams = useLocaleParams();
  const closeMobile = useCloseMobile();
  const { isMobile } = useSidebar();
  const router = useRouter();
  const logout = useServerFn(logoutFn);
  const hasReadLatestReleaseNotes = useHasReadLatestReleaseNotes();
  const unreadTicketCount = useUnreadTicketCount();
  const badgeCount = (hasReadLatestReleaseNotes ? 0 : 1) + unreadTicketCount;
  const { enabled: darkModeEnabled, isDark, toggleDark } = useTheme();

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    void router.navigate({
      to: "/$locale/{-$schoolyear}/login",
      params: localeLinkParams,
    });
  };

  const initials =
    [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join("").toUpperCase() ||
    user.email?.[0]?.toUpperCase() ||
    "?";
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                aria-label={m["common.aria.open_menu"]()}
                className={cn(
                  "data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground border border-transparent",
                  badgeCount > 0 ? "border-border" : "",
                )}
              />
            }
          >
            <Avatar>
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{fullName}</span>
              <span className="truncate text-xs">{user.email}</span>
            </div>
            {/* Unread release-notes signal on the closed trigger, so the dot inside the
                dropdown is discoverable without opening the menu. */}
            <AnimatePresence>
              {badgeCount > 0 && (
                <motion.span
                  initial={{ opacity: 0, scale: 0 }}
                  // Delayed springy pop after the chrome settles, so the unread signal draws
                  // the eye on page load. Exit stays instant-ish and delay-free.
                  animate={{
                    opacity: 1,
                    scale: 1,
                    transition: { delay: 1 },
                  }}
                  exit={{ opacity: 0, scale: 0, transition: { duration: 0.15 } }}
                  data-testid="release-notes-unread-trigger-dot"
                  className="inline-flex size-4 shrink-0 items-center justify-center rounded-full
                    bg-linear-to-br from-red-600 to-red-800 text-xs leading-none font-black
                    text-white text-xs absolute -top-1 -right-1"
                >
                  {badgeCount}
                </motion.span>
              )}
            </AnimatePresence>
            <IconMaterialSymbolsExpandAll className="ml-auto" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
            className="w-fit min-w-56"
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar>
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{fullName}</span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                render={
                  <Link
                    to="/$locale/{-$schoolyear}/$school/account-settings"
                    params={schoolLinkParams}
                    onClick={closeMobile}
                  />
                }
              >
                <IconMaterialSymbolsSettings />
                {m["account.account_settings"]()}
              </DropdownMenuItem>
              <DropdownMenuItem
                render={
                  <Link
                    to="/$locale/{-$schoolyear}/$school/release-notes"
                    params={schoolLinkParams}
                    onClick={closeMobile}
                  />
                }
              >
                <span className="relative inline-flex items-center gap-2">
                  <IconMaterialSymbolsNewReleases />
                  {m["release_notes.title"]()}
                  {!hasReadLatestReleaseNotes && (
                    <span
                      data-testid="release-notes-unread-dot"
                      className="absolute -top-1 -right-4 inline-block size-3 rounded-full
                        bg-linear-to-br from-red-600 to-red-800"
                    />
                  )}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                render={
                  <Link
                    to="/$locale/{-$schoolyear}/$school/tickets"
                    params={schoolLinkParams}
                    onClick={closeMobile}
                  />
                }
              >
                <span className="relative inline-flex items-center gap-2">
                  <IconMaterialSymbolsMailOutline />
                  {m["tickets.list.title"]()}
                  {unreadTicketCount > 0 && (
                    <span
                      data-testid="tickets-unread-dot"
                      className="absolute -top-1 -right-4 inline-block size-3 rounded-full
                        bg-linear-to-br from-red-600 to-red-800"
                    />
                  )}
                </span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            {darkModeEnabled && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {/* Two-state toggle over three underlying states — unchecking back to the OS
                      preference clears the override (see util.theme/theme.tsx). */}
                  <DropdownMenuCheckboxItem checked={isDark} onCheckedChange={toggleDark}>
                    <IconMaterialSymbolsDarkMode />
                    {m["common.theme.dark_mode"]()}
                  </DropdownMenuCheckboxItem>
                </DropdownMenuGroup>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={handleLogout}>
                <IconMaterialSymbolsLogout />
                {m["common.actions.logout"]()}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** The reworked portal chrome: everything that used to live in the top nav, as a sidebar. */
export function AppSidebar({ className, ...props }: ComponentProps<typeof Sidebar>) {
  const schoolLinkParams = useSchoolLinkParams();
  const closeMobile = useCloseMobile();
  const isAdminSession = useIsAdminSession();
  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      // Transparent rail: the page-background gradient (on `html`) shows through, so the
      // sidebar blends with the page instead of painting its own flat --sidebar color. The
      // mobile sheet keeps its solid background.
      className={cn("[&_[data-slot=sidebar-inner]]:bg-transparent", className)}
      {...props}
    >
      <SidebarHeader>
        <AdminSidebarBanner />
        {!isAdminSession && (
          <SidebarLogoLink
            className={""}
            to="/$locale/{-$schoolyear}/$school"
            params={schoolLinkParams}
            onClick={closeMobile}
          />
        )}

        <SchoolSwitcher />
        <SchooljaarSelect />
        <LessonAction />
        <SidebarSeparator className={"mt-4 mb-1"} />
      </SidebarHeader>
      <SidebarContent>
        <nav aria-label={m["nav.main_aria"]()}>
          <NavGroup items={MAIN_NAV_ITEMS} />
        </nav>
        <NavHelp />
        <NavSecondary className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
